/**
 * FAQ Data for Green Filter USA
 * Centralized FAQ content for different pages
 */

export interface FAQ {
  id: number;
  question: string;
  answer: string;
}

// Home Page FAQs
export const homeFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How long does a Green Filter last?',
    answer:
      'Green Filters are designed to last for the lifetime of your vehicle when properly cleaned and maintained. Instead of replacing the filter, you can wash, dry, re-oil, and reinstall it.',
  },
  {
    id: 1,
    question: 'Can I wash and reuse my Green Filter?',
    answer:
      'Yes. Green Filters are washable and reusable. Use the proper Green Filter cleaning products, allow the filter to dry completely, apply filter oil, and reinstall it according to the care instructions.',
  },
  {
    id: 2,
    question: 'Do performance air filters improve airflow?',
    answer:
      'Yes. A high-flow performance air filter is designed to help more air move into the engine compared to many standard paper filters. Green Filter is built to support airflow while maintaining strong filtration.',
  },
  {
    id: 3,
    question: 'Where are Green Filters made?',
    answer: 'Green Filters are made in the USA at the company facility in Uniontown, Pennsylvania.',
  },
  {
    id: 4,
    question: 'Will a Green Filter fit my vehicle?',
    answer:
      'Green Filter offers application-specific filters for many cars, trucks, and motorcycles. Use the vehicle search tool to select your year, make, model, and engine to find the correct filter.',
  },
];

// Measure Filter Page FAQs
export const measureFilterFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I measure an air filter correctly?',
    answer:
      'Measure the inside diameter, top outside diameter, base outside diameter, and height of the filter. These air filter measurements help you find the correct replacement or clamp-on filter size.',
  },
  {
    id: 1,
    question: 'What air filter dimensions do I need?',
    answer:
      'The main air filter dimensions you need are inside diameter, outside diameter at the top, outside diameter at the base, and overall height. These measurements help confirm proper fitment.',
  },
  {
    id: 2,
    question: 'How do I measure the inside diameter of an air filter?',
    answer:
      'Measure across the inside opening of the filter flange. Do not measure from the outside edges of the flange, because that can give an incorrect inside diameter.',
  },
  {
    id: 3,
    question: 'Do I include the flange when measuring air filter height?',
    answer:
      'No. Measure the height from the base of the filter body to the top of the filter, including the end cap, but do not include the flange.',
  },
  {
    id: 4,
    question: 'Can I find an air filter by size or dimensions?',
    answer:
      'Yes. Once you have the correct air filter measurements, you can search by size or contact Green Filter for help finding the right filter for your application.',
  },
];

// Shop by Vehicle Page FAQs
export const shopByVehicleFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I find the right engine air filter for my vehicle?',
    answer:
      'Use the Green Filter vehicle finder at the top of this page. Select your vehicle’s year, make, model, and engine to see compatible Green Filter applications.',
  },
  {
    id: 1,
    question: 'Can I search for an air filter by part number?',
    answer:
      'Yes. If you already know an OEM, Green Filter, or compatible competitor part number, use the part-number search or the air filter cross-reference tool.',
  },
  {
    id: 2,
    question: 'Are Green Filter engine air filters reusable?',
    answer:
      'Yes. Green Filter air filters are designed to be cleaned, serviced, and reused rather than routinely thrown away and replaced.',
  },
  {
    id: 3,
    question: 'Where are Green Filter air filters made?',
    answer: 'Green Filter manufactures its performance air filters in the USA.',
  },
  {
    id: 4,
    question: 'How often should I clean my Green Filter?',
    answer:
      'Under normal driving conditions, Green Filter generally recommends cleaning around every 30,000 miles and inspecting the filter during routine maintenance. Dusty, off-road, or unusually dirty driving conditions may require more frequent service.',
  },
  {
    id: 5,
    question: 'How long does a reusable Green Filter last?',
    answer:
      'Green Filter air filters are designed for long-term use when properly cleaned, serviced, and maintained. Inspect the filter regularly and follow the recommended maintenance instructions.',
  },
  {
    id: 6,
    question: 'How do I know if a Green Filter fits my vehicle?',
    answer:
      'Enter your exact year, make, model, and engine into the vehicle finder. Review the matching application and confirm the product details before ordering.',
  },
  {
    id: 7,
    question: 'What are the benefits of a reusable engine air filter?',
    answer:
      'A reusable engine air filter can be cleaned and serviced instead of routinely discarded. Green Filter products combine reusable construction, vehicle-specific applications, filtration, and a high-flow design intended for long-term use.',
  },
  {
    id: 8,
    question: 'Does Green Filter make air filters for trucks and performance cars?',
    answer:
      'Yes. Green Filter offers applications for cars, pickup trucks, motorcycles, performance vehicles, supercars, and other specialty applications. Availability depends on the specific year, make, model, and engine.',
  },
];

// Universal Cylinder Page FAQs
export const universalCylinderFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I choose the right cone air filter size?',
    answer:
      'Measure the inlet diameter, filter height, base outside diameter, and top outside diameter. Use the size chart to compare these measurements and find the right cone air filter for your intake setup.',
  },
  {
    id: 1,
    question: 'What is a clamp-on air filter?',
    answer:
      'A clamp-on air filter attaches directly to an intake tube using a clamp. It is commonly used for custom intakes, universal air filter setups, and performance applications.',
  },
  {
    id: 2,
    question: 'What is the difference between a cone air filter and a cylinder air filter?',
    answer:
      'A cone air filter is usually wider at the base and narrower at the top, while a cylinder air filter has a similar diameter at the top and base. The right choice depends on available space and intake fitment.',
  },
  {
    id: 3,
    question: 'Do you offer 3-inch and 4-inch air intake filters?',
    answer:
      'Yes. Green Filter offers multiple 3-inch and 4-inch air intake filter options, along with other inlet sizes for custom and universal air filter applications.',
  },
  {
    id: 4,
    question: 'What is a dual cone air filter?',
    answer:
      'A dual cone air filter has filter media on more than one surface to help increase the airflow area. It is commonly used in performance intake setups where extra airflow capacity is needed.',
  },
];

// Universal Round Page FAQs
export const universalRoundFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I choose the right round air filter size?',
    answer:
      "Measure your current filter's outside diameter, inside diameter, and height. Then compare those measurements with the round air filter size chart to find the closest matching Green Filter.",
  },
  {
    id: 1,
    question: 'What measurements do I need for a round air filter?',
    answer:
      'The main measurements are outside diameter, inside diameter, and height. These dimensions help confirm the correct fit for your engine, air cleaner assembly, or custom intake setup.',
  },
  {
    id: 2,
    question: 'Can I find a round air filter by size?',
    answer:
      'Yes. Use the size chart to compare filter number, outside diameter, inside diameter, height, shape, and color. This helps you find a round air filter by size without guessing.',
  },
  {
    id: 3,
    question: 'What is the difference between a round, oval, and rectangular air filter?',
    answer:
      'A round air filter has a circular shape, while oval and rectangular air filters are designed for different air cleaner housings or custom fitment needs. Always match the shape and dimensions to your setup.',
  },
  {
    id: 4,
    question: 'Are Green Filter round air filters washable and reusable?',
    answer:
      'Yes. Green Filter round air filters are washable and reusable. They can be cleaned, re-oiled, and reinstalled for long-term performance and reduced replacement waste.',
  },
];

// Air Cleaner Page FAQs
export const airCleanerFaqs: FAQ[] = [
  {
    id: 0,
    question: 'What is an air cleaner assembly?',
    answer:
      'An air cleaner assembly is the complete housing and filter setup that helps deliver clean air to the engine. It usually includes the air cleaner housing, filter element, mounting area, and related components.',
  },
  {
    id: 1,
    question: 'How do I choose the right air cleaner assembly size?',
    answer:
      'Compare the height, assembly mounting diameter, assembly type, installed height, and color with your current setup. Matching these measurements helps you choose the correct air cleaner assembly for your application.',
  },
  {
    id: 2,
    question: 'What is an air cleaner filter element?',
    answer:
      'An air cleaner filter element is the filter inside the assembly that helps trap dust, dirt, and debris before air enters the engine. A clean filter element helps support airflow and protect the engine.',
  },
  {
    id: 3,
    question: 'What is the difference between an air cleaner assembly and an air filter?',
    answer:
      'An air filter is the replaceable filter element, while an air cleaner assembly includes the full housing or mounting setup that holds the filter in place.',
  },
  {
    id: 4,
    question: 'Are Green Filter air cleaner assemblies washable and reusable?',
    answer:
      'Yes. Green Filter air cleaner assemblies use washable, reusable filter elements that can be cleaned, re-oiled, and reinstalled for long-term performance.',
  },
];

// Universal Panel Page FAQs
export const universalPanelFaqs: FAQ[] = [
  {
    id: 0,
    question: 'What is a universal panel air filter?',
    answer:
      'A panel air filter is a flat, rectangular filter that fits inside your factory air box as a direct OEM replacement. It uses the stock housing, so no permanent modifications are needed.',
  },
  {
    id: 1,
    question: 'How do I choose the right panel filter size?',
    answer:
      'Compare the length, width, and height of your factory panel filter with the measurements listed in the table above. Matching all three dimensions helps ensure a proper fit in your air box.',
  },
  {
    id: 2,
    question: 'Will a panel filter fit my stock air box?',
    answer:
      'Yes. Green Filter panel filters are designed as direct-fit replacements for the factory paper filter, so they install in the stock air box without any cutting or modification.',
  },
  {
    id: 3,
    question: 'Are Green Filter panel filters washable and reusable?',
    answer:
      'Yes. Green Filter panel filters are washable and reusable. Clean, dry, and re-oil the filter using genuine Green Filter cleaning products, then reinstall it for continued use.',
  },
  {
    id: 4,
    question: 'Do panel filters improve horsepower?',
    answer:
      'A high-flow panel filter is designed to improve airflow into the engine compared to a restrictive stock paper filter, which can support better throttle response and performance.',
  },
];

// Cross Reference Brand Page FAQs
export const crossReferenceBrandFaqs: FAQ[] = [
  {
    id: 0,
    question: 'What is an air filter cross-reference?',
    answer:
      'An air filter cross-reference helps match another brand\'s filter number with a compatible replacement filter. It is useful when you want to compare part numbers and find a Green Filter option.',
  },
  {
    id: 1,
    question: 'How do I use the Green Filter interchange tool?',
    answer:
      'Select the filter brand or company, enter the filter code, and check the available Green Filter products. Make sure the final filter matches your vehicle or application before ordering.',
  },
  {
    id: 2,
    question: 'Can I cross-reference a K&N air filter?',
    answer:
      'Yes. You can use the tool to search for a Green Filter interchange for K&N air filter numbers when a compatible option is available.',
  },
  {
    id: 3,
    question: 'Can I cross-reference FRAM or WIX air filters?',
    answer:
      'Yes. The air filter cross-reference tool can help compare FRAM, WIX, and other aftermarket filter numbers with available Green Filter options.',
  },
  {
    id: 4,
    question: 'What if I cannot find my filter code?',
    answer:
      'If your filter code does not return a match, contact Green Filter support for help. You can also search by vehicle or confirm measurements to find the correct filter.',
  },
];

// Cross Reference Filters Page FAQs
export const crossReferenceFiltersFaqs: FAQ[] = [
  {
    id: 0,
    question: 'What is a Green Filter cross-reference?',
    answer:
      'A Green Filter cross-reference helps compare a Green Filter part number with compatible replacement numbers from other air filter brands.',
  },
  {
    id: 1,
    question: 'How do I use the Green Filter cross-reference tool?',
    answer:
      'Select your Green Filter part number from the dropdown to view available cross-reference filters from other brands.',
  },
  {
    id: 2,
    question: 'Can I find other brand replacements for a Green Filter?',
    answer:
      'Yes. When available, the tool shows other brand filter numbers that may cross-reference with the selected Green Filter.',
  },
  {
    id: 3,
    question: 'What if my Green Filter part number is not listed?',
    answer:
      'Contact Green Filter support for help or use the vehicle selector to search by year, make, model, and engine.',
  },
  {
    id: 4,
    question: 'Should I confirm fitment before ordering a replacement filter?',
    answer:
      'Yes. Always confirm the filter dimensions, vehicle application, and fitment before ordering any replacement or cross-reference filter.',
  },
];

// About Us Page FAQs
export const aboutUsFaqs: FAQ[] = [
  {
    id: 0,
    question: 'Where are Green Filters made?',
    answer:
      'Green Filters are made in the USA. Green Filter USA focuses on American manufacturing, quality construction, and performance-focused air filter design.',
  },
  {
    id: 1,
    question: 'Is Green Filter USA a manufacturer?',
    answer:
      'Yes. Green Filter USA is a performance air filter manufacturer that designs and builds washable, reusable air filters for cars, trucks, motorcycles, custom intakes, and motorsports applications.',
  },
  {
    id: 2,
    question: 'Are Green Filters washable and reusable?',
    answer:
      'Yes. Green Filters are washable and reusable. They can be cleaned, re-oiled, and reinstalled for long-term performance instead of being replaced like standard disposable paper filters.',
  },
  {
    id: 3,
    question: 'Who uses Green Filter products?',
    answer:
      'Green Filter products are used by automotive enthusiasts, daily drivers, engine builders, racing teams, and performance professionals who want strong airflow, filtration, and long-term value.',
  },
  {
    id: 4,
    question: 'What makes Green Filter different?',
    answer:
      'Green Filter combines USA-made quality, reusable filter design, high-flow performance, reliable filtration, and racing experience to create air filters built for both street and motorsports applications.',
  },
];

// Guarantee Page FAQs
export const guaranteeFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How long is the Green Filter warranty?',
    answer:
      'Green High Performance Air Filters are designed to last for the lifetime of your vehicle and are covered by a Limited Lifetime Warranty when properly cleaned, oiled, and maintained.',
  },
  {
    id: 1,
    question: 'How do I register my Green Filter warranty?',
    answer:
      'Complete the warranty registration form on this page or mail the warranty card included with your product within 30 days of purchase.',
  },
  {
    id: 2,
    question: 'How do I make a warranty claim?',
    answer:
      'Download and complete the Warranty Return Form. The form includes instructions for returning your filter to Green Filter for review.',
  },
  {
    id: 3,
    question: 'Do I need an RGA number for a return or exchange?',
    answer:
      'Yes. You must call Green Filter for an RGA number before sending any filter back. Filters sent without an RGA number may not be exchanged.',
  },
  {
    id: 4,
    question: 'Can I use other oils on my Green Filter?',
    answer:
      'No. Do not use motor oil, transmission fluid, WD-40®, or any other brand of filter oil. Use only genuine Green Filter Oil to avoid damaging the filter.',
  },
];

// Why Go Green Page FAQs
export const whyGoGreenFaqs: FAQ[] = [
  {
    id: 0,
    question: 'Why choose Green Filter?',
    answer:
      'Green Filter is a good choice for drivers who want a washable, reusable, high-flow air filter made in the USA. Green Filter air filters are designed to support better airflow, strong filtration, long-term performance, and reduced waste compared with disposable paper filters.',
  },
  {
    id: 1,
    question: 'What are the main Green Filter benefits?',
    answer:
      'The main Green Filter benefits include improved airflow, reusable design, strong filtration, custom-molded fitment, long-term value, and a Limited Lifetime Warranty. Green Filter is built for cars, trucks, motorcycles, and performance applications.',
  },
  {
    id: 2,
    question: 'Does an air filter increase horsepower?',
    answer:
      'A performance air filter may help increase horsepower by improving airflow to the engine, but results depend on the vehicle, engine setup, intake system, and driving conditions. Green Filter high-flow air filters are designed to help the engine breathe more freely while maintaining reliable filtration.',
  },
  {
    id: 3,
    question: 'What are high-flow air filter benefits?',
    answer:
      'High-flow air filter benefits may include better engine breathing, improved throttle response, reusable performance, reduced replacement costs, and less waste over time. A high-flow reusable air filter can be a strong upgrade from a standard disposable paper filter.',
  },
  // {
  //   id: 4,
  //   question: 'How does Green Filter compare to K&N?',
  //   answer:
  //     'When comparing Green Filter vs K&N, drivers should look at airflow, filtration, fitment, reusability, cleaning process, warranty, and where the filter is made. Green Filter focuses on USA-made washable performance air filters with application-specific fitment and long-term value.',
  // },
];

// Private Label Page FAQs
export const privateLabelFaqs: FAQ[] = [
  {
    id: 0,
    question: 'Do you make private label air filters?',
    answer:
      'Yes. Green Filter USA manufactures private label air filters for brands, distributors, performance shops, and specialty applications.',
  },
  {
    id: 1,
    question: 'Can Green Filter make custom air filters to my specifications?',
    answer:
      'Yes. Green Filter can build custom air filters based on your specifications, including shape, size, inlet style, mesh type, color, and branding needs.',
  },
  {
    id: 2,
    question: 'What custom air filter options are available?',
    answer:
      'Custom options may include offset inlets, non-standard shapes, heavy-duty mesh, threaded inserts, custom colors, personalized end caps, customized text, multiple inlets, angled inlets, and dry flow oil-free filter designs.',
  },
  {
    id: 3,
    question: 'How much do custom private label air filters cost?',
    answer:
      'Pricing depends on filter size, design complexity, materials, branding needs, setup requirements, and order quantity.',
  },
  {
    id: 4,
    question: 'How do I request a custom air filter quote?',
    answer:
      'To request a quote, contact Green Filter USA with your filter dimensions, application details, branding needs, color preferences, inlet style, and estimated order quantity.',
  },
];

// Pedal Power Kit Page FAQs
export const pedalPowerKitFaqs: FAQ[] = [
  {
    id: 0,
    question: 'What is the Pedal Power Kit?',
    answer:
      'The Pedal Power Kit is the ultimate performance bundle that combines the Green Filter Throttle Controller with a Green Filter High Performance Air Filter. Together, they unlock a whole new world of performance and efficiency for your vehicle.',
  },
  {
    id: 1,
    question: 'What are the four driving modes?',
    answer:
      'The throttle controller features four Bluetooth-controlled modes: ECO mode for safe driving and fuel efficiency (up to 20% improvement), CITY mode for active throttle response, SPORT mode for engaged driving (up to 70% throttle response), and SPORT+ mode for extreme performance recommended for track use.',
  },
  {
    id: 2,
    question: 'Will this void my vehicle warranty?',
    answer:
      'No, the Pedal Power Kit will not void your vehicle warranty. Both components are non-invasive and can be easily removed if needed.',
  },
  {
    id: 3,
    question: 'How do I control the different modes?',
    answer:
      'The throttle controller is Bluetooth compatible, allowing you to change modes directly from your phone using our free mobile app, available on both App Store and Google Play.',
  },
  {
    id: 4,
    question: 'Is it suitable for off-road driving?',
    answer:
      'Yes! The Pedal Power Kit is suitable for offroad and 4x4 applications. It provides better traction on ice, sand, mud, and snow while delivering better fuel mileage.',
  },
  {
    id: 5,
    question: 'How is it customized for my vehicle?',
    answer:
      'The throttle controller features custom programming for each specific vehicle, ensuring optimal performance and compatibility with your make and model.',
  },
  {
    id: 6,
    question: 'What is a throttle controller?',
    answer:
      'A throttle controller is a device that adjusts the signal between your gas pedal and the throttle body, improving throttle response and allowing you to customize how your vehicle accelerates. It does not add horsepower but makes the existing power more accessible.',
  },
  {
    id: 7,
    question: 'How does ECO mode save fuel?',
    answer:
      'ECO mode reduces throttle sensitivity, requiring more pedal input for the same acceleration. This helps prevent aggressive acceleration habits and can improve fuel efficiency by up to 20% depending on driving conditions.',
  },
];

// Cleaner Care Page FAQs
export const cleanerCareFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I clean a reusable air filter?',
    answer:
      'Remove loose dirt, spray the filter with air filter cleaner, let it soak for about 15 minutes, rinse gently with warm water from the clean side to the dirty side, allow it to air dry naturally, then re-oil before reinstalling.',
  },
  {
    id: 1,
    question: 'How often should I clean my Green Filter?',
    answer:
      'Inspect your Green Filter at every oil change. Under normal conditions, clean it about every 30,000 miles, or sooner if you drive in dusty or dirty environments.',
  },
  {
    id: 2,
    question: 'How do I oil an air filter after cleaning?',
    answer:
      'After the filter is completely dry, apply Green Filter Oil evenly using about one spray per 2 square inches of filter media. Do not over-oil the filter.',
  },
  {
    id: 3,
    question: 'Can I use any oil on my Green Filter?',
    answer:
      'No. Use only genuine Green Filter Oil. Do not use motor oil, transmission fluid, WD-40®, or another brand of filter oil because it may damage the filter.',
  },
  {
    id: 4,
    question: 'Do I need an air filter cleaner and oil kit?',
    answer:
      'Yes, if you have a washable, reusable air filter. A cleaner and oil kit helps remove buildup, restore airflow, and re-oil the filter properly for long-term performance.',
  },
];

// Harley Filter Page FAQs
export const harleyFilterFaqs: FAQ[] = [
  {
    id: 0,
    question: 'How do I find the right Harley air filter?',
    answer:
      'Use the filter table to compare inlet diameter, height, base outside diameter, top outside diameter, style, end cap, inlet type, and color. Matching these measurements helps you choose the correct Harley air filter for your setup.',
  },
  {
    id: 1,
    question: 'What measurements do I need for a motorcycle cone air filter?',
    answer:
      'For a motorcycle cone air filter, check the inlet diameter, filter height, outside diameter at the base, and outside diameter at the top. These measurements help confirm proper fitment before ordering.',
  },
  {
    id: 2,
    question: 'How do I clean a Harley air filter?',
    answer:
      'Remove the Harley air filter, apply the proper filter cleaner, rinse gently, let it dry completely, then re-oil it before reinstalling. Always follow the filter care instructions and avoid reinstalling the filter while it is still wet.',
  },
  {
    id: 3,
    question: 'How often should I clean a Harley Davidson air filter?',
    answer:
      'Cleaning frequency depends on riding conditions. For normal use, inspect the filter regularly and clean it when dirt buildup is visible. Dusty roads, long rides, or performance use may require more frequent cleaning.',
  },
  {
    id: 4,
    question: 'Are Green Filter Harley air filters washable and reusable?',
    answer:
      'Yes. Green Filter Harley air filters are washable and reusable. They can be cleaned, re-oiled, and reinstalled for long-term use, helping reduce replacement waste while supporting airflow and filtration.',
  },
];

// Ford Focus Brand Page FAQs
export const focusFaqs: FAQ[] = [
  {
    id: 0,
    question: 'Does Green Filter make a 2015 Ford Focus air filter?',
    answer:
      'Yes. Green Filter\'s washable air filter fits the 2015 Ford Focus, along with 2007-2017 Focus, ST, and RS models. Check the year and engine columns in the product table above to confirm the correct part for your Focus.',
  },
  {
    id: 1,
    question: 'What years does this Ford Focus air filter fit?',
    answer:
      'This Ford Focus air filter fits 2007 through 2017 Focus models, including the ST and RS. Use the product table above to match your model year and engine to the correct part number.',
  },
  {
    id: 2,
    question: 'Is a washable filter a good Ford Focus air filter replacement?',
    answer:
      'Yes. Instead of replacing a paper filter every 12,000-15,000 miles, a Green Filter washable air filter can be cleaned, re-oiled, and reused for the life of your Focus, and it\'s backed by a Limited Lifetime Warranty.',
  },
];

// Porsche Brand Page FAQs
export const porscheFaqs: FAQ[] = [
  {
    id: 0,
    question: 'Does Green Filter make a Porsche 911 air filter replacement?',
    answer:
      "Yes. Green Filter's washable Porsche 911 air filter fits 996, 997, and other 911 generations. Instead of buying a new paper filter, you can clean, re-oil, and reinstall it as your Porsche 911 air filter replacement.",
  },
  {
    id: 1,
    question: 'Is there a washable Porsche Boxster or Cayman air filter replacement?',
    answer:
      'Yes. Green Filter\'s washable Porsche Boxster and Cayman air filters cover the 986, 987, and 981 chassis codes. Wash, re-oil, and reinstall it instead of replacing a paper filter every service interval.',
  },
  {
    id: 2,
    question: 'Does Green Filter make a Porsche 928 or 997 air filter?',
    answer:
      'Yes. Green Filter offers a washable, reusable air filter for the Porsche 928 and 997, engineered as a high-flow performance engine air filter for your Porsche.',
  },
  {
    id: 3,
    question: 'Is a washable filter a good Porsche air filter replacement?',
    answer:
      "Yes. Instead of replacing a paper filter every service interval, a Green Filter washable air filter can be cleaned, re-oiled, and reused for the life of your Porsche, and it's backed by a Limited Lifetime Warranty.",
  },
];
